/**
 * Uma caixa que se planifica sobre a malha, como função PURA da abertura.
 *
 * A caixa fica apoiada no "chão" — uma malha quadriculada vista em perspectiva
 * cavaleira. As quatro faces laterais giram em torno das arestas da base até deitarem
 * na malha; a tampa, presa à face de trás, gira junto e desdobra-se em sequência. Com
 * a abertura em 100%, a planificação em cruz está inteira sobre a malha, e cada
 * aresta pode ser CONTADA em quadradinhos — é para isso que o seletor existe.
 *
 * Perspectiva cavaleira, e não isométrica: as linhas da malha na direção x continuam
 * horizontais e com o tamanho verdadeiro, e contar quadradinhos na planificação não
 * depende de adivinhar um encurtamento.
 */

import type { Vec2 } from '../geom/vec.ts';
import type { Rect, Scene, Shape } from './scene.ts';
import { boundsOf, scene } from './scene.ts';
import type { RichText } from '../schema/richtext.ts';

interface P3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Tamanho de um quadradinho, em unidades de desenho. */
const U = 20;
/**
 * Quanto a profundidade (y) avança para a direita e para cima na tela.
 *
 * "Câmera" ALTA: numa versão anterior, com a profundidade subindo só 0,34 por
 * quadradinho, a malha vista de quase rente ao chão achatava a planificação, e contar
 * as faces deitadas ficava difícil. Com 0,62, é como olhar a mesa de cima, de leve em
 * diagonal: a planificação aparece aberta, e a caixa fechada ainda se lê como sólido.
 */
const KX = 0.42;
const KY = 0.62;

const projetar = (p: P3): Vec2 => ({ x: U * (p.x + KX * p.y), y: U * (p.z + KY * p.y) });

export interface Dimensoes {
  /** Comprimento (x), largura (y) e altura (z), em quadradinhos. */
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

/** As seis faces na abertura `s` ∈ [0, 1], em 3D. */
function faces(d: Dimensoes, s: number): { readonly nome: string; readonly pts: readonly P3[] }[] {
  const { a, b, c } = d;
  const fi = (s * Math.PI) / 2;
  const sf = Math.sin(fi);
  const cf = Math.cos(fi);
  // A tampa gira junto com a face de trás e, ao mesmo tempo, abre em relação a ela:
  // começa deitada sobre a caixa (apontando para a frente) e termina deitada na malha,
  // continuando a face de trás.
  const gama = Math.PI - Math.PI * s;
  const dir = { y: Math.cos(gama), z: Math.sin(gama) };
  const dobra = (x: number): P3 => ({ x, y: b + c * sf, z: c * cf });
  const tampa = (x: number, t: number): P3 => {
    const h = dobra(x);
    return { x, y: h.y + t * dir.y, z: h.z + t * dir.z };
  };
  return [
    {
      nome: 'base',
      pts: [
        { x: 0, y: 0, z: 0 },
        { x: a, y: 0, z: 0 },
        { x: a, y: b, z: 0 },
        { x: 0, y: b, z: 0 },
      ],
    },
    {
      nome: 'esquerda',
      pts: [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: b, z: 0 },
        { x: -c * sf, y: b, z: c * cf },
        { x: -c * sf, y: 0, z: c * cf },
      ],
    },
    { nome: 'tras', pts: [{ x: 0, y: b, z: 0 }, { x: a, y: b, z: 0 }, dobra(a), dobra(0)] },
    { nome: 'tampa', pts: [tampa(0, 0), tampa(a, 0), tampa(a, b), tampa(0, b)] },
    {
      nome: 'direita',
      pts: [
        { x: a, y: 0, z: 0 },
        { x: a, y: b, z: 0 },
        { x: a + c * sf, y: b, z: c * cf },
        { x: a + c * sf, y: 0, z: c * cf },
      ],
    },
    {
      nome: 'frente',
      pts: [
        { x: 0, y: 0, z: 0 },
        { x: a, y: 0, z: 0 },
        { x: a, y: -c * sf, z: c * cf },
        { x: 0, y: -c * sf, z: c * cf },
      ],
    },
  ];
}

/** Extensão da malha: a planificação inteira, com um quadradinho de folga. */
function limitesDaMalha(d: Dimensoes): { x0: number; x1: number; y0: number; y1: number } {
  return { x0: -d.c - 1, x1: d.a + d.c + 1, y0: -d.c - 1, y1: 2 * d.b + d.c + 1 };
}

function malha(d: Dimensoes): Shape[] {
  const { x0, x1, y0, y1 } = limitesDaMalha(d);
  const out: Shape[] = [];
  for (let i = x0; i <= x1; i++) {
    out.push({
      t: 'seg',
      a: projetar({ x: i, y: y0, z: 0 }),
      b: projetar({ x: i, y: y1, z: 0 }),
      role: 'auxiliary',
    });
  }
  for (let j = y0; j <= y1; j++) {
    out.push({
      t: 'seg',
      a: projetar({ x: x0, y: j, z: 0 }),
      b: projetar({ x: x1, y: j, z: 0 }),
      role: 'auxiliary',
    });
  }
  return out;
}

/**
 * Enquadramento FIXO para todas as aberturas: a união das posições da caixa ao longo
 * do movimento. Com viewBox calculado quadro a quadro, a figura mudaria de tamanho
 * enquanto o aluno arrasta o seletor.
 */
function quadroFixo(d: Dimensoes, extra: readonly Shape[]): Rect {
  const pts: Shape[] = [...malha(d), ...extra];
  for (let k = 0; k <= 10; k++) {
    for (const f of faces(d, k / 10))
      pts.push({ t: 'poly', pts: f.pts.map(projetar), closed: true, role: 'given' });
  }
  return boundsOf(pts, 0.06);
}

export function caixaPlanificando(
  d: Dimensoes,
  s: number,
  legenda: RichText,
  a11y: { title: string; desc: string },
): Scene {
  const { x0, y0 } = limitesDaMalha(d);
  // A ESCALA É O PRÓPRIO QUADRADINHO DO CANTO, destacado, com o tamanho escrito logo
  // abaixo da aresta da frente. Uma cota à parte, com linhas de chamada perpendiculares
  // na tela, não acompanhava a inclinação da malha e parecia medir outra coisa.
  const c0 = projetar({ x: x0, y: y0, z: 0 });
  const c1 = projetar({ x: x0 + 1, y: y0, z: 0 });
  const c2 = projetar({ x: x0 + 1, y: y0 + 1, z: 0 });
  const c3 = projetar({ x: x0, y: y0 + 1, z: 0 });
  // O segundo rótulo corre PARALELO à aresta que entra na profundidade: os dois lados
  // do quadradinho medem o mesmo, e a malha é de fato quadriculada — a perspectiva é que
  // encurta e inclina um deles.
  const dx = c3.x - c0.x;
  const dy = c3.y - c0.y;
  const L = Math.hypot(dx, dy) || 1;
  const giro = Math.round((Math.atan2(dy, dx) * 180) / Math.PI);
  const fora = { x: -dy / L, y: dx / L };
  const escala: Shape[] = [
    { t: 'poly', pts: [c0, c1, c2, c3], closed: true, role: 'highlight', fill: 'result-fill' },
    {
      t: 'label',
      at: { x: (c0.x + c1.x) / 2, y: c0.y - 4 },
      text: legenda,
      anchor: 's',
      emphasis: 'axis',
    },
    {
      t: 'label',
      at: { x: (c0.x + c3.x) / 2 + fora.x * 12, y: (c0.y + c3.y) / 2 + fora.y * 12 },
      text: legenda,
      anchor: 'c',
      rotate: giro,
      emphasis: 'axis',
    },
  ];
  const formas: Shape[] = [...malha(d)];
  for (const f of faces(d, s)) {
    formas.push({
      t: 'poly',
      pts: f.pts.map(projetar),
      closed: true,
      role: 'given',
      fill: f.nome === 'base' ? 'given-fill' : 'result-fill',
    });
  }
  formas.push(...escala);
  return scene(formas, { a11y, viewBox: quadroFixo(d, escala) });
}
